import React from 'react';
import type { DailyNoteRecord, TaskRecord } from '../api';
import { formatDisplayDate } from '../utils/date';

interface Props {
  completedTasks: TaskRecord[];
  recentNotes: DailyNoteRecord[];
  overdueTasks: TaskRecord[];
  onSelectTask: (task: TaskRecord) => void;
  onOpenNote: (date: string) => void;
}

export function ReviewView({ completedTasks, recentNotes, overdueTasks, onSelectTask, onOpenNote }: Props) {
  return (
    <div className="review-view" style={{ display: 'grid', gap: 16 }}>
      <div className="summary-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <SummaryCard label="Completed this week" value={completedTasks.length.toString()} />
        <SummaryCard label="Recent notes" value={recentNotes.length.toString()} />
        <SummaryCard label="Still overdue" value={overdueTasks.length.toString()} />
      </div>

      <section
        style={{
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)',
          padding: 16,
        }}
      >
        <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Week summary</h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
          Use this screen to answer three questions: what moved forward, what stayed stuck, and what should be scheduled next.
        </p>
      </section>

      <div className="summary-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
        <section
          style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            padding: 16,
          }}
        >
          <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Completed tasks</h2>
          {completedTasks.length === 0 ? (
            <EmptyState message="Nothing completed in the last seven days yet." />
          ) : (
            <div style={{ display: 'grid', gap: 8 }}>
              {completedTasks.map((task) => (
                <button
                  key={task.id}
                  className="task-card done"
                  style={{ width: '100%', textAlign: 'left', background: 'var(--bg-primary)' }}
                  onClick={() => onSelectTask(task)}
                >
                  <div className="task-content">
                    <div className="task-title">{task.title}</div>
                    <div className="task-meta">
                      <span>
                        {task.completedAt
                          ? new Date(task.completedAt).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
                          : 'Completed'}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>

        <section
          style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            padding: 16,
          }}
        >
          <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Recent notes</h2>
          {recentNotes.length === 0 ? (
            <EmptyState message="No notes logged this week yet." />
          ) : (
            <div style={{ display: 'grid', gap: 8 }}>
              {recentNotes.map((entry) => (
                <button
                  key={entry.id}
                  className="task-card"
                  style={{ width: '100%', textAlign: 'left', background: 'var(--bg-primary)' }}
                  onClick={() => onOpenNote(entry.noteDate)}
                >
                  <div className="task-content">
                    <div className="task-title">{entry.title || formatDisplayDate(entry.noteDate, { weekday: 'short', month: 'short', day: 'numeric' })}</div>
                    <div className="task-meta">
                      <span>{formatDisplayDate(entry.noteDate, { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                    </div>
                    {entry.content && (
                      <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
                        {truncate(entry.content)}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <section
      style={{
        background: 'var(--bg-secondary)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: 16,
      }}
    >
      <div style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, marginTop: 4 }}>{value}</div>
    </section>
  );
}

function EmptyState({ message }: { message: string }) {
  return <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>{message}</p>;
}

function truncate(value: string): string {
  return value.length > 140 ? `${value.slice(0, 137)}...` : value;
}
